import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { IActivo } from 'modelos/src';
import { IParamEventoUsuario } from '../../../../standalone/config-evento-usuario/crear-editar-config-evento-usuario-dialog/crear-editar-config-evento-usuario-dialog.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { retry, Subscription, throwError, timer } from 'rxjs';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';

@Component({
  selector: 'app-detalles-colectivo',
  templateUrl: './detalles-colectivo.component.html',
  styleUrl: '../../../../../componentes/detalle/detalle-pagina.scss',
  standalone: false,
})
export class DetallesColectivoComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private mapaFacilService = inject(MapaFacilService);

  public selectedTab = 0;
  public idColectivo: string | null = null;
  public colectivo?: IActivo;
  public loading: boolean = true;
  private colectivo$?: Subscription;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  public params?: IParamEventoUsuario;

  // El mismo permiso que pide el listado para editar
  public puedeEditar = this.helper.tienePermiso('Colectivos - Editar');

  /** Estado operativo con los colores del marco (como en vehículos). */
  public claseEstado(estado: string): string {
    if (estado === 'Operativo') return 'ok';
    if (estado === 'En mantenimiento') return 'advertencia';
    if (estado === 'Fuera de servicio') return 'alerta';
    return 'neutro';
  }

  /**
   * Las pestañas en orden, por nombre: Trackeos y Log Trackeos solo para
   * nivel 0. Tiene que coincidir con la plantilla.
   */
  private pestanas(): string[] {
    return [
      'info',
      'viajes',
      'eventos',
      'codigos',
      'eventosPersonalizados',
      'notas',
      'contactos',
      'documentos',
      ...(this.helper.esNivel0() ? ['trackeos', 'logTrackeos'] : []),
      'servicioTecnico',
    ];
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  private async listarColectivo() {
    return new Promise<void>((resolve) => {
      this.colectivo$?.unsubscribe();
      // Si el colectivo no existe (se borró, no hay acceso) el getById falla:
      // sin el error la página quedaba con el spinner para siempre.
      this.colectivo$ = this.lista2
        .colectivo(this.idColectivo, 30)
        // Un request que falla terminaba el refresco de cada 30 s para
        // siempre. Si todavía no se cargó nunca, el error sigue de largo.
        .pipe(
          retry({
            delay: (error) =>
              this.colectivo ? timer(30000) : throwError(() => error),
          }),
        )
        .subscribe({
          next: (data) => {
            // Las pestañas leen el colectivo al crearse: un objeto nuevo cada
            // 30 s no las recarga. Info lo sigue con ngOnChanges.
            this.colectivo = data;
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  public tabChanged(event: MatTabChangeEvent): void {
    // Al salir de Viajes y Paradas se detiene la animación del viaje
    // (selectedTab todavía es la pestaña de la que se sale).
    if (this.selectedTab === this.pestanas().indexOf('viajes')) {
      this.mapaFacilService.stopTripAnimation();
    }
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public editar(): void {
    this.router.navigate([
      'main/colectivos/colectivos/editar',
      this.idColectivo,
    ]);
  }

  /**
   * Carga el colectivo del id de la ruta. Se llama cada vez que cambia el
   * id: Angular reutiliza el componente al ir de un colectivo a otro.
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.colectivo = undefined;
    this.idColectivo = id;
    await this.listarColectivo();

    // Los parametros para el componente config evento usuario
    this.params = {
      tipoEntidad: 'Activo',
      agrupacion: 'Entidad',
      idEntidad: this.idColectivo,
      botonCrear: HelperService.getConfigCliente().moduloColectivos?.activo,
      activo: this.colectivo,
      crear: 'Dialog',
    };
    this.loading = false;
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.colectivo$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
